// District-wide intersection priority list (GET /api/safety/intersections).
// Not corridor-scoped: clusters every junction crash in the state's snapshot
// into physical intersections and ranks them by comprehensive crash cost
// (the state's table — MassDOT 2024 for Massachusetts), by MassDOT's tempered
// HSIP EPDO points, by raw crash count, or — with the CMF library — by the
// expected annual benefit of the best-rated intersection countermeasure
// (roundabout conversion, Elvik 2017 meta-analysis) applied to each row's own
// K/A/B/C mix. Row click flies the map when a corridor map is open.
import React, { useEffect, useMemo, useState } from 'react';
import * as api from './safetyApi';
import { fmtUsd } from './insightsText';
import { eligibleEntries, exposureYears, intersectionBenefit } from './cmfBenefit';
import { Stars } from './CmfCard';
import TopNSelect from './TopNSelect';

const DISTRICTS = ['', '1', '2', '3', '4', '5', '6'];
const RANKS = [
  { key: 'cost', label: 'Comprehensive cost' },
  { key: 'epdo', label: 'HSIP EPDO points' },
  { key: 'crashes', label: 'Crash count' },
  { key: 'benefit', label: 'Countermeasure benefit' },
];

const LIMITS = [10, 25, 50, 100, 250, 500];
const BENEFIT_FETCH = 500; // benefit ranking re-sorts the statewide top-N by cost

/** Years from a dataset version like "MA-v2.1-2021-2026" (fallback only). */
const yearsFromVersion = (v) => {
  const m = /(\d{4})-(\d{4})$/.exec(v || '');
  if (!m) return [];
  const out = [];
  for (let y = +m[1]; y <= +m[2]; y += 1) out.push(y);
  return out;
};

export default function IntersectionsPanel({ stateId, onFocus, library, policy = '45', datasetYears, showCmf = true,
  fetchIntersections = api.fetchIntersections }) {
  // fetchIntersections: the Safety Overview passes its own (same payload, its own permission)
  const [district, setDistrict] = useState('1');
  const [rank, setRank] = useState('cost');
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openRow, setOpenRow] = useState(null);

  const serverRank = rank === 'benefit' ? 'cost' : rank;
  const serverLimit = rank === 'benefit' ? BENEFIT_FETCH : limit;
  useEffect(() => {
    if (!stateId) return;
    setLoading(true);
    setError(null);
    setOpenRow(null);
    fetchIntersections(stateId, district, serverRank, serverLimit)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [stateId, district, serverRank, serverLimit]);

  // best-rated intersection countermeasure under the policy (roundabout at 4-5★)
  const entry = useMemo(() => {
    const list = eligibleEntries(library, 'ISD', policy);
    return list.length ? [...list].sort((a, b) => b.stars - a.stars)[0] : null;
  }, [library, policy]);
  const cmfOn = showCmf && !!entry;

  const rows = useMemo(() => {
    if (!data) return [];
    const years = data.datasetYears || datasetYears || yearsFromVersion(data.datasetVersion);
    const exposure = exposureYears(years) || 1;
    const costs = data.costTable?.values || {};
    const withBenefit = data.intersections.map((r) => ({
      ...r, benefit: cmfOn ? intersectionBenefit(r.mix, entry, costs, exposure) : null,
    }));
    if (rank !== 'benefit') return withBenefit;
    return withBenefit.sort((a, b) => (b.benefit?.benefitPerYr || 0) - (a.benefit?.benefitPerYr || 0))
      .slice(0, limit).map((r, i) => ({ ...r, rank: i + 1 }));
  }, [data, datasetYears, cmfOn, entry, rank, limit]);

  if (!stateId) return null;

  return (
    <div className="safety-insights">
      <div className="safety-insights-nav">
        <label className="safety-filter">
          <span>District</span>
          <select value={district} onChange={(e) => setDistrict(e.target.value)}>
            {DISTRICTS.map((d) => (
              <option key={d} value={d}>{d ? `District ${d}` : 'Statewide'}</option>
            ))}
          </select>
        </label>
        <label className="safety-filter">
          <span>Rank by</span>
          <select value={rank} onChange={(e) => setRank(e.target.value)}>
            {RANKS.filter((r) => r.key !== 'benefit' || cmfOn).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
        <label className="safety-filter">
          <span>Show</span>
          {/* the server ranks at most 500; fewer when the district has fewer intersections */}
          <TopNSelect value={limit} onChange={setLimit} presets={LIMITS} allowAll={false} hardMax={500}
            max={data ? Math.min(data.intersectionCount || 500, 500) : null} className="" />
        </label>
        {data && (
          <span className="safety-muted">
            {data.intersectionCount} intersections from {data.junctionCrashes} junction
            crashes · dataset {data.datasetVersion}
            {rank === 'benefit' ? ` · benefit ranking over the top ${BENEFIT_FETCH} by cost` : ''}
          </span>
        )}
      </div>

      {loading && <div className="safety-muted">Clustering intersections… (first load sweeps the statewide snapshot)</div>}
      {error && <div className="safety-error">Intersections unavailable: {error}</div>}

      {data && !loading && (
        <>
          <div className="safety-table-scroll">
            <table className="safety-table safety-table-clickable">
              <thead>
                <tr>
                  <th /><th>#</th><th>Cost</th><th>EPDO</th><th>Crashes</th>
                  <th>K/A/B/C/O</th><th>Major road</th><th>Secondary road</th>
                  <th>City / Town</th><th>Lat, Lon</th><th>Junction</th>
                  {cmfOn && (
                    <>
                      <th title={entry.appliesTo}>Countermeasure</th>
                      <th title="Expected annual benefit of the countermeasure at this intersection">Benefit / yr</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <React.Fragment key={r.rank}>
                  <tr
                    className={r.mix.K ? 'safety-row-hit' : ''}
                    onClick={() => onFocus && onFocus({
                      lat: r.lat, lng: r.lng, zoom: 16,
                      key: `ix-${r.rank}-${Date.now()}`,
                    })}
                    title="Show on map (when a corridor map is open)">
                    <td className="safety-expander"
                      onClick={(e) => { e.stopPropagation(); setOpenRow(openRow === r.rank ? null : r.rank); }}
                      title="Show the crashes at this intersection">
                      {(r.crashList || []).length ? 'ⓘ' : ''}
                    </td>
                    <td>{r.rank}</td>
                    <td><strong>{fmtUsd(r.cost)}</strong></td>
                    <td>{r.epdo}</td>
                    <td>{r.crashes}</td>
                    <td>{`${r.mix.K}/${r.mix.A}/${r.mix.B}/${r.mix.C}/${r.mix.O}`}</td>
                    <td>{r.major}</td>
                    <td>{r.secondary || '?'}</td>
                    <td>{(r.city || '').replace(/\w\S*/g, (w) => w[0] + w.slice(1).toLowerCase())}</td>
                    <td>{r.lat.toFixed(5)}, {r.lng.toFixed(5)}</td>
                    <td>{r.junction}</td>
                    {cmfOn && (
                      <>
                        <td className="safety-cell-wrap">{entry.name} · CMF {entry.cmf}{entry.cmfBySeverity?.K ? ` (K ${entry.cmfBySeverity.K})` : ''} <Stars entry={entry} /></td>
                        <td><span className="safety-benefit">{fmtUsd(r.benefit?.benefitPerYr || 0)}</span></td>
                      </>
                    )}
                  </tr>
                  {openRow === r.rank && (r.crashList || []).length > 0 && (
                    <tr className="safety-site-crashes">
                      <td colSpan={cmfOn ? 13 : 11}>
                        <ul>
                          {r.crashList.map((c) => (
                            <li key={c.id}
                              onClick={() => onFocus && onFocus({
                                lat: c.lat, lng: c.lon, zoom: 17,
                                key: `ixcrash-${c.id}-${Date.now()}`,
                              })}
                              title="Show this crash on the map">
                              <span className={`safety-sev safety-sev-${c.kabco === 'K' ? 'severe' : (c.kabco === 'A' || c.kabco === 'B') ? 'moderate' : 'minor'}`}>
                                {c.kabco}
                              </span>
                              {' '}{c.datetime ? c.datetime.slice(0, 16).replace('T', ' ') : '—'}
                              {' · '}{c.manner || 'crash'}
                              {' · '}{c.firstHarmful || '—'}
                              {c.light && c.light !== 'Daylight' ? ` · ${c.light}` : ''}
                              {c.surface && c.surface !== 'Dry' ? ` · ${c.surface}` : ''}
                              {c.numVehicles ? ` · ${c.numVehicles} veh` : ''}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="safety-muted safety-insight-source">
            Cost basis: {data.costTable?.source}. EPDO uses MassDOT's tempered HSIP
            weights — every fatal-or-injury crash (K/A/B/C) counts {data.epdoWeights?.K} points
            and a property-damage-only crash counts {data.epdoWeights?.O}, deliberately NOT
            proportional to crash costs so rankings don't chase individual fatal crashes.
            Rows with a fatality are highlighted; on the cost ranking one fatal crash
            outweighs dozens of injury crashes — compare with the EPDO ranking.
            {cmfOn && (
              <> Benefit / yr applies the {entry.name.toLowerCase()} CMF (FHWA Clearinghouse
                {' '}{entry.crfid}, {entry.stars}★) to each row's own K/A/B/C mix over the dataset's
                exposure years; intersections with RDV ISD data on a loaded corridor also get the
                low-cost sight-triangle option in the corridor tables.</>
            )}
          </p>
        </>
      )}
    </div>
  );
}
